// frontend/src/components/auditor/AuditorActivityLog.js
// Audit log viewer with filtering for the auditor portal.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const fmtDate = (v) => v ? new Date(v).toLocaleString() : '—';

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
  VIEW: 'bg-gray-100 text-gray-600',
  LOGIN: 'bg-blue-100 text-blue-700',
  LOGOUT: 'bg-slate-100 text-slate-600',
  EXPORT: 'bg-purple-100 text-purple-700',
  SEND: 'bg-teal-100 text-teal-700',
  APPROVE: 'bg-emerald-100 text-emerald-700',
  REJECT: 'bg-orange-100 text-orange-700',
};

export default function AuditorActivityLog({ authAxios, dateRange }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const data = await fetchAllPages(authAxios, 'audit-logs/');
    setLogs(data);
    if (data.length === 0) {
      setFetchError('No activity log data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Unique action types for filter
  const actionTypes = [...new Set(logs.map(l => l.action).filter(Boolean))].sort();

  // Filter by selected action type
  const filtered = actionFilter ? logs.filter(l => l.action === actionFilter) : logs;

  // Stats
  const todayCount = logs.filter(l => {
    if (!l.timestamp && !l.created_at) return false;
    const d = new Date(l.timestamp || l.created_at);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Activity Log</h2>
        <p className="text-sm text-gray-500 mt-1">Complete audit trail of all actions performed in the system.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Entries', value: logs.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Today', value: todayCount, color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Filtered', value: filtered.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Action Types', value: actionTypes.length, color: 'text-purple-700 bg-purple-50 border-purple-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Action type filter */}
      {actionTypes.length > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600">Filter by action:</span>
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setActionFilter('')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                !actionFilter ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {actionTypes.map(a => (
              <button
                key={a}
                onClick={() => setActionFilter(a === actionFilter ? '' : a)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  actionFilter === a ? 'bg-teal-600 text-white' : (ACTION_COLORS[a] || 'bg-gray-100 text-gray-600') + ' hover:opacity-80'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      )}

      <AuditorTable
        title={`Audit Log (${filtered.length})`}
        data={filtered}
        columns={[
          { key: 'timestamp', label: 'Timestamp', render: (v, row) => fmtDate(v || row.created_at) },
          { key: 'user_username', label: 'User', render: (v, row) =>
            row.user_username || row.user_details?.username || row.user_email || '—'
          },
          { key: 'action', label: 'Action', render: (v) => (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>
              {v || '—'}
            </span>
          )},
          { key: 'content_type', label: 'Entity', render: (v, row) => row.content_type || row.entity_type || '—' },
          { key: 'description', label: 'Description' },
          { key: 'ip_address', label: 'IP Address' },
        ]}
        exportCsv={{ filename: 'audit_log.csv' }}
        emptyMessage="No audit log entries found."
        pageSize={50}
      />
    </div>
  );
}
